import { useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';

/**
* Renders the profile skeleton layout with placeholder cards styled by the current theme.
* @example
* ProfileSkeleton()
* <div className="min-h-screen">…</div>
* @param {{void}} {{}} - No arguments required.
* @returns {{JSX.Element}} A themed skeleton structure for the profile page components.
*/
const ProfileSkeleton = () => {
  const { theme } = useContext(ThemeContext);

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: theme.background.primary }}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar Skeleton */}
          <div className="lg:col-span-4">
            <div 
              className="rounded-lg p-6 space-y-6"
              style={{ 
                backgroundColor: theme.background.card,
                boxShadow: theme.shadow.md
              }}
            >
              {/* Avatar */}
              <div className="text-center">
                <div 
                  className="size-32 mx-auto rounded-full animate-pulse mb-4"
                  style={{ backgroundColor: theme.background.secondary }}
                />
                <div 
                  className="h-6 rounded w-32 mx-auto mb-2 animate-pulse"
                  style={{ backgroundColor: theme.background.secondary }}
                />
                <div 
                  className="h-4 rounded w-24 mx-auto mb-4 animate-pulse"
                  style={{ backgroundColor: theme.background.secondary }}
                />
                <div 
                  className="h-10 rounded w-full animate-pulse"
                  style={{ backgroundColor: theme.background.secondary }}
                />
              </div>

              {/* Social Links */}
              <div 
                className="space-y-3 pt-4"
                style={{ borderTop: `1px solid ${theme.border.primary}` }}
              >
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-2">
                    <div 
                      className="size-5 rounded animate-pulse"
                      style={{ backgroundColor: theme.background.secondary }}
                    />
                    <div 
                      className="h-4 rounded w-32 animate-pulse"
                      style={{ backgroundColor: theme.background.secondary }}
                    />
                  </div>
                ))}
              </div>

              {/* Community Stats */}
              <div 
                className="pt-4"
                style={{ borderTop: `1px solid ${theme.border.primary}` }}
              >
                <div 
                  className="h-5 rounded w-32 mb-4 animate-pulse"
                  style={{ backgroundColor: theme.background.secondary }}
                />
                <div className="space-y-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="flex items-center gap-3">
                      <div 
                        className="size-8 rounded animate-pulse"
                        style={{ backgroundColor: theme.background.secondary }}
                      />
                      <div className="flex-1">
                        <div 
                          className="h-4 rounded w-24 mb-1 animate-pulse"
                          style={{ backgroundColor: theme.background.secondary }}
                        />
                        <div 
                          className="h-3 rounded w-16 animate-pulse"
                          style={{ backgroundColor: theme.background.secondary }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div 
                className="pt-4"
                style={{ borderTop: `1px solid ${theme.border.primary}` }}
              >
                <div 
                  className="h-5 rounded w-24 mb-4 animate-pulse"
                  style={{ backgroundColor: theme.background.secondary }}
                />
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex justify-between">
                      <div 
                        className="h-4 rounded w-20 animate-pulse"
                        style={{ backgroundColor: theme.background.secondary }}
                      />
                      <div 
                        className="h-4 rounded w-24 animate-pulse"
                        style={{ backgroundColor: theme.background.secondary }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Skeleton */}
          <div className="lg:col-span-8 space-y-6">
            {/* Problem Stats */}
            <div 
              className="rounded-lg p-6 h-64 animate-pulse"
              style={{ 
                backgroundColor: theme.background.card,
                boxShadow: theme.shadow.md
              }}
            >
              <div className="grid grid-cols-2 gap-8 h-full">
                <div className="flex items-center justify-center">
                  <div 
                    className="size-40 rounded-full"
                    style={{ backgroundColor: theme.background.secondary }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <div 
                      key={i}
                      className="rounded-lg p-4"
                      style={{ backgroundColor: theme.background.secondary }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Badges */}
            <div 
              className="rounded-lg p-6 h-48 animate-pulse"
              style={{ 
                backgroundColor: theme.background.card,
                boxShadow: theme.shadow.md
              }}
            >
              <div 
                className="h-6 rounded w-24 mb-4"
                style={{ backgroundColor: theme.background.secondary }}
              />
              <div 
                className="h-32 rounded"
                style={{ backgroundColor: theme.background.secondary }}
              />
            </div>

            {/* Heatmap */}
            <div 
              className="rounded-lg p-6 h-64 animate-pulse"
              style={{ 
                backgroundColor: theme.background.card,
                boxShadow: theme.shadow.md
              }}
            >
              <div 
                className="h-6 rounded w-64 mb-4"
                style={{ backgroundColor: theme.background.secondary }}
              />
              <div 
                className="h-40 rounded"
                style={{ backgroundColor: theme.background.secondary }}
              />
            </div>

            {/* Recent AC */}
            <div 
              className="rounded-lg p-6 h-96 animate-pulse"
              style={{ 
                backgroundColor: theme.background.card,
                boxShadow: theme.shadow.md
              }}
            >
              <div className="flex gap-4 mb-6">
                {[1, 2, 3, 4].map(i => (
                  <div 
                    key={i}
                    className="h-8 rounded w-24"
                    style={{ backgroundColor: theme.background.secondary }}
                  />
                ))}
              </div>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div 
                    key={i}
                    className="h-12 rounded"
                    style={{ backgroundColor: theme.background.secondary }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSkeleton;
